'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ClinicalDocument {
  id: string;
  title: string;
  category: 'fda' | 'research' | 'trial' | 'certification';
  date: string;
  fileSize: string;
  downloadUrl: string;
}

interface ClinicalEvidenceQuickAccessProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const ClinicalEvidenceQuickAccess = ({
  isOpen = false,
  onClose
}: ClinicalEvidenceQuickAccessProps) => {
  const [isModalOpen, setIsModalOpen] = useState(isOpen);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
  { id: 'all', label: 'All Documents', icon: 'DocumentTextIcon' },
  { id: 'fda', label: 'FDA Approvals', icon: 'ShieldCheckIcon' },
  { id: 'research', label: 'Peer-Reviewed', icon: 'AcademicCapIcon' },
  { id: 'trial', label: 'Clinical Trials', icon: 'BeakerIcon' },
  { id: 'certification', label: 'Certifications', icon: 'CheckBadgeIcon' }];


  const documents: ClinicalDocument[] = [
  {
    id: '1',
    title: 'CDSCO Clearance - REVIVE System',
    category: 'fda',
    date: '2024-11-15',
    fileSize: '2.4 MB',
    downloadUrl: '/documents/fda-clearance.pdf'
  },
  {
    id: '2',
    title: 'Multi-Center Clinical Trial Results - NICU Outcomes',
    category: 'trial',
    date: '2024-10-22',
    fileSize: '5.8 MB',
    downloadUrl: '/documents/clinical-trial.pdf'
  },
  {
    id: '3',
    title: 'Peer-Reviewed Study: Neonatal Brain Protection Efficacy',
    category: 'research',
    date: '2024-09-18',
    fileSize: '3.2 MB',
    downloadUrl: '/documents/peer-review.pdf'
  },
  {
    id: '4',
    title: 'ISO 13485 Medical Device Quality Certification',
    category: 'certification',
    date: '2024-08-30',
    fileSize: '1.6 MB',
    downloadUrl: '/documents/iso-certification.pdf'
  },
  {
    id: '5',
    title: 'CE Mark Certification - European Medical Device Regulation',
    category: 'certification',
    date: '2024-08-15',
    fileSize: '1.9 MB',
    downloadUrl: '/documents/ce-mark.pdf'
  },
  {
    id: '6',
    title: 'Long-term Safety Study - 24-Month Follow-up Data',
    category: 'research',
    date: '2024-07-10',
    fileSize: '4.5 MB',
    downloadUrl: '/documents/safety-study.pdf'
  }];


  const handleOpen = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    if (onClose) onClose();
  };

  const handleDownload = (doc: ClinicalDocument) => {
    console.log(`Downloading: ${doc.title}`);
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <>
      








      {isModalOpen &&
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div
          className="absolute inset-0 bg-background"
          onClick={handleClose} />

          
          <div className="relative bg-card rounded-lg shadow-card max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-2xl font-bold font-heading text-foreground">
                  Clinical Evidence Library
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Access peer-reviewed research, FDA documentation, and clinical trial results
                </p>
              </div>
              <button
              onClick={handleClose}
              className="p-2 rounded-lg hover:bg-muted transition-colors duration-250 ease-out"
              aria-label="Close modal">

                <Icon name="XMarkIcon" size={24} className="text-foreground" />
              </button>
            </div>

            <div className="p-6 border-b border-border">
              <div className="relative">
                <Icon
                name="MagnifyingGlassIcon"
                size={20}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />

                <input
                type="text"
                placeholder="Search clinical documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-250 ease-out" />

              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {categories.map((category) =>
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-250 ease-out ${
                selectedCategory === category.id ?
                'bg-primary text-primary-foreground' :
                'bg-muted text-foreground hover:bg-muted/80'}`
                }>

                    <Icon name={category.icon as any} size={16} />
                    <span>{category.label}</span>
                  </button>
              )}
              </div>
            </div>

            <div className="overflow-y-auto max-h-[calc(90vh-280px)] p-6">
              {filteredDocuments.length === 0 ?
            <div className="text-center py-12">
                  <Icon name="DocumentTextIcon" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No documents found matching your criteria</p>
                </div> :

            <div className="space-y-3">
                  {filteredDocuments.map((doc) =>
              <div
                key={doc.id}
                className="flex items-center justify-between p-4 bg-surface rounded-lg hover:shadow-card transition-all duration-250 ease-out">

                      <div className="flex items-start space-x-4 flex-1">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <Icon name="DocumentTextIcon" size={24} className="text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-foreground">{doc.title}</h3>
                          <div className="flex items-center space-x-4 mt-1 text-sm text-muted-foreground">
                            <span>{new Date(doc.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                            <span>•</span>
                            <span>{doc.fileSize}</span>
                          </div>
                        </div>
                      </div>
                      <button
                  onClick={() => handleDownload(doc)}
                  className="ml-4 p-2 bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 transition-all duration-250 ease-out"
                  aria-label={`Download ${doc.title}`}>

                        <Icon name="ArrowDownTrayIcon" size={20} />
                      </button>
                    </div>
              )}
                </div>
            }
            </div>
          </div>
        </div>
      }
    </>);

};

export default ClinicalEvidenceQuickAccess;